import {CreatePlanNode, ToggleNodeProcessor} from "../view-model";
import {findNodeByCaId} from "./findNodeByCaId";
import {cloneHierarchy} from "./cloneHierarchy";
import {findPathToNode} from "./findPathToNode";

/**
 * Recursively selects a node and all its parent nodes
 */
const selectNodeAndParents = (
  node: CreatePlanNode,
  caId: string,
  parents: CreatePlanNode[] = []
): boolean => {
  // Check if this is the target node
  if (node.caId === caId) {
    // Select this node
    node.selected = true;
    // Select all parents in the stack
    parents.forEach(parent => (parent.selected = true));
    return true;
  }

  // Check children
  return node.children.some(child =>
    selectNodeAndParents(child, caId, [...parents, node])
  );
};

/**
 * Recursively deselects a node and all its children
 */
const deselectNodeAndChildren = (node: CreatePlanNode): void => {
  node.selected = false;
  node.children.forEach(deselectNodeAndChildren);
};

/**
 * Deselects contiguous immutable ancestor nodes when a node is deselected,
 * but only those not in the selection path. Traversal stops at the first
 * mutable ancestor or when the selection path is encountered.
 */
const deselectImmutableAncestors = (
  root: CreatePlanNode,
  from: string,
  caId: string
): void => {
  // Get the selection path (protected zone) from 'from' node to root
  const selectionPath = findPathToNode(root, from);
  const selectionPathCaIds = new Set(selectionPath.map(node => node.caId));

  // Get the path from the deselected node to root
  const nodePath = findPathToNode(root, caId);
  if (nodePath.length === 0) return;

  // Walk up the tree (excluding the target node itself) and deselect only the
  // contiguous sequence of immutable ancestors that are NOT in the selection path.
  // Stop at the first mutable ancestor or if we reach the selection path.
  for (let i = nodePath.length - 2; i >= 0; i--) {
    const ancestor = nodePath[i];

    // Stop if we reached the selection path (do not deselect protected nodes).
    if (selectionPathCaIds.has(ancestor.caId)) {
      break;
    }

    if (ancestor.immutable) {
      ancestor.selected = false;
      // continue to check the next ancestor up while they remain immutable
      continue;
    }

    // Encountered a mutable ancestor; stop traversal.
    break;
  }
};

/**
 * Processes node selection with the following simple hierarchy creation rules:
 * 1. Any node marked as immutable cannot be toggled
 * 2. When selecting a node, all parent nodes are automatically selected
 * 3. When deselecting a node, all child nodes are automatically deselected
 * 4. When deselecting a node, deselect only contiguous immutable ancestor nodes
 *    not in the selection path, stopping at the first mutable or selection-path ancestor
 *
 * @param root The root of the current hierarchy creation plan
 * @param from The id of the node from which the hierarchy is being derived;
 *             the path from this node to the root defined the selection path
 * @param caId The id of the node being toggled
 * @param selected true if the node is being selected; false if deselected
 * @returns A clone of the plan with the updated selection state; undefined if no changes were made
 */
export const toggleNodeSelection: ToggleNodeProcessor = (
  root: CreatePlanNode,
  from: string,
  caId: string,
  selected: boolean
): CreatePlanNode | undefined => {
  // Clone the plan to avoid modifying the original
  const clone = cloneHierarchy(root);

  // Find the node by caId
  const node = findNodeByCaId(clone, caId);

  // Rule 1: Immutable nodes cannot be toggled
  if (!node || node.immutable) {
    return;
  }

  if (selected) {
    // Rule 2: When selecting a node, ensure all parent nodes are selected
    selectNodeAndParents(clone, caId);
  } else {
    // Rule 3: When deselecting a node, deselect all child nodes
    deselectNodeAndChildren(node);
    // Rule 4: When deselecting a node, deselect only contiguous immutable ancestor nodes
    deselectImmutableAncestors(clone, from, caId);
  }

  return clone;
};
